import { useState, useEffect, useRef } from "react";
import { MapPin, Search } from "lucide-react";
import { useMapboxToken } from "@/hooks/useMapboxToken";
import { toast } from "sonner";
import { PropertyData } from "@/types/wizard";

interface Step1AddressProps {
  propertyData: PropertyData | null;
  onPropertySelect: (data: PropertyData) => void;
  isDark: boolean;
}

const Step1Address = ({ propertyData, onPropertySelect, isDark }: Step1AddressProps) => {
  const [address, setAddress] = useState(propertyData?.address || "");
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { token } = useMapboxToken();
  const containerRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const searchAddress = async (query: string) => {
    if (query.length < 3 || !token) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      // Limit search to California Bay Area
      const bbox = "-122.8,36.9,-121.5,37.9";
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${token}&country=US&bbox=${bbox}&types=address&limit=5`
      );
      const data = await response.json();
      setSuggestions(data.features || []);
    } catch (error) {
      console.error("Geocoding error:", error);
      toast.error("Failed to search address. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectSuggestion = (suggestion: any) => {
    const [lng, lat] = suggestion.center;
    const context = suggestion.context || [];
    
    const city = context.find((c: any) => c.id.startsWith("place"))?.text;
    const county = context.find((c: any) => c.id.startsWith("district"))?.text;
    const zipCode = context.find((c: any) => c.id.startsWith("postcode"))?.text;

    const data: PropertyData = {
      address: suggestion.place_name,
      city,
      county,
      zipCode,
      latitude: lat,
      longitude: lng,
    };

    onPropertySelect(data);
    setAddress(suggestion.place_name);
    setSuggestions([]);
  };

  const inputBg = isDark ? "#0a0a0a" : "#ffffff";
  const shadowStyle = isDark
    ? "inset 4px 4px 8px #000000, inset -4px -4px 8px #141414"
    : "inset 4px 4px 8px #d1d1d1, inset -4px -4px 8px #ffffff";

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 
          className="mb-2 text-3xl md:text-4xl font-normal"
          style={{ color: "#E4683B" }}
        >
          Where is your property located?
        </h2>
        <p 
          className="opacity-80 text-xl"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          Enter your address to discover your ADU potential
        </p>
      </div>

      <div className="max-w-xl mx-auto relative" ref={containerRef}>
        <div
          className="flex items-center gap-3 p-4 rounded-2xl"
          style={{
            backgroundColor: inputBg,
            boxShadow: shadowStyle,
          }}
        >
          <MapPin className="w-6 h-6 flex-shrink-0" style={{ color: "#E4683B" }} />
          <input
            type="text"
            value={address}
            onChange={(e) => {
              setAddress(e.target.value);
              searchAddress(e.target.value);
            }}
            placeholder="Enter your property address..."
            className="flex-1 bg-transparent outline-none text-lg"
            style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
          />
          {isLoading ? (
            <div 
              className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" 
              style={{ borderColor: "#E4683B" }} 
            />
          ) : (
            <Search className="w-6 h-6 flex-shrink-0 opacity-50" />
          )}
        </div>

        {/* Suggestions Dropdown */}
        {suggestions.length > 0 && (
          <div
            className="absolute top-full left-0 right-0 mt-2 rounded-xl overflow-hidden z-50"
            style={{
              backgroundColor: isDark ? "#0f0f0f" : "#ffffff",
              boxShadow: isDark
                ? "8px 8px 16px #000000, -8px -8px 16px #141414"
                : "8px 8px 16px #d1d1d1, -8px -8px 16px #ffffff",
            }}
          >
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.id}
                onClick={() => handleSelectSuggestion(suggestion)}
                className="w-full text-left px-4 py-3 hover:opacity-80 transition-opacity border-b last:border-b-0"
                style={{
                  borderColor: isDark ? "#1a1a1a" : "#e5e5e5",
                  color: isDark ? "#ffffff" : "#1a1a1a",
                }}
              >
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: "#E4683B" }} />
                  <div>
                    <p className="font-medium">{suggestion.text}</p>
                    <p className="text-sm opacity-60">{suggestion.place_name}</p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        <p 
          className="text-center mt-4 text-sm opacity-60"
          style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
        >
          We currently serve San Mateo and Santa Clara counties in California
        </p>
      </div>

      {/* Show selected property info */}
      {propertyData && (
        <div 
          className="max-w-xl mx-auto p-4 rounded-xl mt-6"
          style={{
            backgroundColor: isDark ? "#0a0a0a" : "#ffffff",
            boxShadow: isDark
              ? "4px 4px 8px #000000, -4px -4px 8px #141414"
              : "4px 4px 8px #d1d1d1, -4px -4px 8px #ffffff",
          }}
        >
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-5 h-5" style={{ color: "#E4683B" }} />
            <span 
              className="font-medium"
              style={{ color: "#E4683B" }}
            >
              Selected Property
            </span>
          </div>
          <p 
            className="text-sm"
            style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
          >
            {propertyData.address}
          </p>
          {propertyData.city && (
            <p 
              className="text-sm opacity-70 mt-1"
              style={{ color: isDark ? "#ffffff" : "#1a1a1a" }}
            >
              {propertyData.city}, {propertyData.county}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default Step1Address;
